<template>
  <v-card
    class="d-flex flex-column fill-height"
    rounded="lg"
  >
    <v-card-title class="pa-6">
      <span class="subtitle-2 grey--text text--grey-100">{{ $t('card_title.match_timeline') }}</span>
      <v-spacer />
      <v-btn
        v-if="removable"
        icon
        style="pointer-events: auto"
        @click="$emit('remove')"
      >
        <v-icon small>
          fal fa-times
        </v-icon>
      </v-btn>
    </v-card-title>

    <v-card-text class="pa-6 d-flex flex-column flex-grow-1">
      <div class="my-auto py-12">
        <div class="mx-4 position-relative">
          <div
            v-for="marker in timeMarkers"
            :key="`marker-${marker}`"
            class="position-absolute text-center"
            :style="{ left: `${(marker / maxTime) * 100}%`, transform: 'translateX(-50%)', top: '0px' }"
          >
            <v-divider
              vertical
              class="mx-auto"
              style="height: 8px"
            />
            <div class="caption grey--text">
              {{ marker }}'
            </div>
          </div>

          <v-progress-linear
            value="100"
            height="6"
            color="green lighten-3"
            background-color="green lighten-5"
            rounded
          />

          <div
            v-for="(group, actualTime) in groupedEvents"
            :key="`event-${actualTime}`"
            class="position-absolute"
            :style="{ left: `${(actualTime / maxTime) * 100}%`, top: '50%', transform: 'translate(-50%, -50%)', zIndex: 2 }"
          >
            <div class="d-flex flex-column-reverse align-center">
              <v-tooltip
                v-for="(event, i) in group"
                :key="i"
                top
              >
                <template #activator="{ on, attrs }">
                  <v-avatar
                    :color="getEvent(event.type).color"
                    size="28"
                    class="elevation-2 my-1"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon
                      x-small
                      color="white"
                    >
                      {{ getEvent(event.type).icon }}
                    </v-icon>
                  </v-avatar>
                </template>
                <span><strong>{{ formatTime(event.timeObj) }}</strong> {{ event.playerName }}</span>
              </v-tooltip>
            </div>
          </div>
        </div>
      </div>

      <div class="mt-auto pt-6 border-top">
        <v-row
          dense
          justify="center"
        >
          <v-col
            v-for="type in ['goal', 'own-goal', 'yellow-card', 'red-card']"
            :key="type"
            cols="auto"
          >
            <v-chip
              outlined
            >
              <v-avatar
                left
                :color="getEvent(type).color"
              >
                <v-icon
                  x-small
                  color="white"
                >
                  {{ getEvent(type).icon }}
                </v-icon>
              </v-avatar>
              {{ $t(`scoresheet.${getEvent(type).key}`) }}
            </v-chip>
          </v-col>
        </v-row>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: 'MatchTimeline',
  props: {
    players: {
      type: Array,
      required: true
    },
    removable: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    allEvents() {
      const events = []
      const config = [
        { key: 'goal_times', type: 'goal' },
        { key: 'own_goal_times', type: 'own-goal' },
        { key: 'yellow_card_times', type: 'yellow-card' }
      ]

      this.players.forEach(p => {
        const s = p.player_stats || {}
        const name = `${p.first_name[0]}. ${p.last_name}`

        config.forEach(cfg => {
          (s[cfg.key] || []).forEach(timeObj => {
            const minute = typeof timeObj === 'number' ? timeObj : timeObj.minute_full
            const actualTime = typeof timeObj === 'number' ? timeObj : (timeObj.minute_full + (timeObj.stoppage_time || 0))
            events.push({ minute, actualTime, timeObj, type: cfg.type, playerName: name })
          })
        })

        if (s.red_card && s.yellow_card_times?.length) {
          const lastYellow = s.yellow_card_times[s.yellow_card_times.length - 1]
          const minute = typeof lastYellow === 'number' ? lastYellow : lastYellow.minute_full
          const actualTime = typeof lastYellow === 'number' ? lastYellow : (lastYellow.minute_full + (lastYellow.stoppage_time || 0))
          events.push({ minute, actualTime, timeObj: lastYellow, type: 'red-card', playerName: name })
        }
      })
      return events.sort((a, b) => a.minute - b.minute)
    },
    maxTime() {
      const maxEventTime = this.allEvents.length > 0
        ? Math.max(...this.allEvents.map(e => e.actualTime))
        : 90
      return Math.max(90, Math.ceil(maxEventTime / 15) * 15)
    },
    timeMarkers() {
      const markers = []
      const interval = 15
      for (let i = 0; i <= this.maxTime; i += interval) {
        markers.push(i)
      }
      return markers
    },
    groupedEvents() {
      return this.allEvents.reduce((acc, ev) => {
        (acc[ev.actualTime] = acc[ev.actualTime] || []).push(ev)
        return acc
      }, {})
    }
  },
  methods: {
    formatTime(timeObj) {
      if (!timeObj) return ''
      if (typeof timeObj === 'number') return `${timeObj}'`
      const base = `${timeObj.minute_full}'`
      return timeObj.stoppage_time ? `${base}+${timeObj.stoppage_time}` : base
    },
    getEvent(type) {
      const map = {
        goal: { color: 'success', icon: 'fas fa-futbol', key: 'goals' },
        'own-goal': { color: 'error', icon: 'fas fa-futbol', key: 'own_goals' },
        'yellow-card': { color: 'warning', icon: 'fas fa-rectangle-vertical', key: 'yellow_cards' },
        'red-card': { color: 'red darken-2', icon: 'fas fa-rectangle-vertical', key: 'red_cards' }
      }
      return map[type]
    }
  }
}
</script>

<style scoped>
.position-relative { position: relative; }
.position-absolute { position: absolute; }
</style>
